// Shared design primitives so every module speaks the same visual language
// (see /app/frontend/DESIGN.md and design_guidelines.json). Import from here
// instead of re-implementing token strings per module.
import { iconFor } from "@/lib/menuIcons";

// ---- tokens ---------------------------------------------------------------
export const LABEL_CLS = "text-xs font-bold tracking-[0.15em] uppercase text-zinc-500";
export const INPUT_CLS =
  "w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-sm text-zinc-100 " +
  "focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors";
export const CARD_CLS = "bg-zinc-900 border border-zinc-800 rounded-xl";

// Module taxonomy (mirror of backend registry MODULE_TYPES).
export const TYPE_LABELS = {
  tool: "Tool", agent: "Agent", library: "Library",
  insights: "Insights", builder: "Builder", config: "Config",
};

// ---- primitives -----------------------------------------------------------
export const SectionLabel = ({ children, className = "" }) => (
  <span className={`${LABEL_CLS} ${className}`}>{children}</span>
);

export const Card = ({ className = "", children, ...rest }) => (
  <div className={`${CARD_CLS} ${className}`} {...rest}>{children}</div>
);

// Standard module header: icon tile + Outfit title + subtitle (+ optional right slot).
export const ModuleHeader = ({ icon = "Circle", title, subtitle, right = null, testId }) => {
  const Icon = iconFor(icon);
  return (
    <div className="flex items-start justify-between gap-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-indigo-600/15 border border-indigo-600/25 flex items-center justify-center">
          <Icon className="h-5 w-5 text-indigo-400" />
        </div>
        <div>
          <h1 className="font-[Outfit] text-2xl font-bold tracking-tight text-zinc-50">{title}</h1>
          {subtitle && <p className="text-sm text-zinc-500">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
};

const BTN_BASE =
  "inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium " +
  "transition-colors disabled:opacity-60 disabled:cursor-not-allowed outline-none " +
  "focus-visible:ring-1 focus-visible:ring-indigo-500";
const BTN_VARIANTS = {
  primary: "bg-indigo-600 hover:bg-indigo-500 text-white",
  secondary: "bg-indigo-500/10 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/20",
  ghost: "border border-zinc-800 text-zinc-300 hover:border-zinc-700",
  danger: "bg-red-600 hover:bg-red-500 text-white",
};
const BTN_SIZES = { sm: "h-8 px-3", md: "h-9 px-4", lg: "h-10 px-5" };

export const Button = ({ variant = "primary", size = "md", className = "", children, ...rest }) => (
  <button className={`${BTN_BASE} ${BTN_VARIANTS[variant] || BTN_VARIANTS.primary} ${BTN_SIZES[size] || BTN_SIZES.md} ${className}`} {...rest}>
    {children}
  </button>
);

export const Input = ({ className = "", ...rest }) => (
  <input className={`${INPUT_CLS} ${className}`} {...rest} />
);

export const Textarea = ({ className = "", ...rest }) => (
  <textarea className={`${INPUT_CLS} resize-none ${className}`} {...rest} />
);

const BADGE_TONES = {
  neutral: "bg-zinc-800 text-zinc-300 border-zinc-700",
  indigo: "bg-indigo-500/10 text-indigo-200 border-indigo-500/30",
  success: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  warning: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  error: "bg-red-500/10 text-red-300 border-red-500/30",
};
export const Badge = ({ tone = "neutral", className = "", children }) => (
  <span className={`text-[11px] font-medium rounded-md border px-2 py-0.5 ${BADGE_TONES[tone] || BADGE_TONES.neutral} ${className}`}>
    {children}
  </span>
);

// Colour-coded SEO score chip — same thresholds as the On-Page SEO Optimiser.
export const ScoreBadge = ({ score, title = "Rank Math score (read-only)" }) => {
  if (score == null) return null;
  const c =
    score >= 75 ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10"
    : score >= 50 ? "text-amber-300 border-amber-500/30 bg-amber-500/10"
    : "text-red-300 border-red-500/30 bg-red-500/10";
  return (
    <span title={title} className={`text-[11px] rounded px-1.5 py-0.5 border flex-shrink-0 ${c}`}>
      {score}/100
    </span>
  );
};

export const EmptyState = ({ icon = "Inbox", title, hint }) => {
  const Icon = iconFor(icon);
  return (
    <div className="text-center py-12 border border-dashed border-zinc-800 rounded-xl">
      <Icon className="h-6 w-6 text-zinc-600 mx-auto mb-3" />
      {title && <p className="text-sm text-zinc-300">{title}</p>}
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </div>
  );
};

// Inline "?" help tooltip. Optionally deep-links to a full guide (opens the Help Centre).
export const HelpTooltip = ({ text, to }) => {
  const Q = iconFor("HelpCircle");
  const body = (
    <span title={text} data-testid="help-tooltip"
      className="inline-flex items-center justify-center h-4 w-4 rounded-full text-zinc-500 hover:text-indigo-300 cursor-help align-middle">
      <Q className="h-3.5 w-3.5" />
    </span>
  );
  if (to) {
    return (
      <a href={to} title={text} data-testid="help-tooltip-link"
        className="inline-flex items-center justify-center h-4 w-4 rounded-full text-zinc-500 hover:text-indigo-300 align-middle">
        <Q className="h-3.5 w-3.5" />
      </a>
    );
  }
  return body;
};

